import { Operand } from '../../../../core/query/entities/operand.entity';
import {
    AdvancedSearchTerm,
    QueryPartStrategyToOperand
} from '../../../../core/query/entities/structured-query.entity';
import { QueryPartData } from './query-part-data.entity';
import { QueryHandleQuotes } from './query-part-handle-quotes';
import { QueryPartStrategy } from './query-part-strategy';

export class QueryPart implements QueryPartData {
    public static readonly mergeableStrategies: QueryPartStrategy[] = [
        QueryPartStrategy.MatchAll,
        QueryPartStrategy.MatchNone
    ];

    private static readonly defaults: Omit<QueryPartData, 'field'> = {
        values: [],
        strategy: QueryPartStrategy.MatchAll
    };

    readonly strategy: QueryPartStrategy;
    readonly field: string;
    readonly values: string[];
    readonly handleQuotes?: QueryHandleQuotes;

    constructor(qp: Pick<QueryPartData, 'field'> & Partial<QueryPartData>) {
        const merged: QueryPartData = { ...QueryPart.defaults, ...qp };
        this.field = merged.field;
        this.strategy = merged.strategy;
        this.values = QueryPart.removeEmpty(merged.values);
        this.handleQuotes = qp.handleQuotes;
    }

    private static removeEmpty(list: (string | undefined)[]): string[] {
        const hasValues: (el: string | undefined) => boolean = (el: string | undefined) => !!el && el.trim().length > 0;
        return list.filter(hasValues) as string[];
    }

    public static merge(qp1: QueryPart, qp2: QueryPart): QueryPart {
        if (qp1.field !== qp2.field) {
            throw new Error('Only Queryparts with equal fields can be merged');
        }

        if (qp1.strategy !== qp2.strategy) {
            throw new Error('Only Queryparts with the same strategy can be merged');
        }

        if (!this.mergeableStrategies.includes(qp1.strategy)) {
            throw new Error(`${qp1.strategy} is not mergable`);
        }

        const queryPart1Values: string[] = QueryPart.removeEmpty(qp1.values);
        const queryPart2Values: string[] = QueryPart.removeEmpty(qp2.values);
        const mergedValues: string[] = [...new Set([...queryPart1Values, ...queryPart2Values])];

        return new QueryPart({ ...qp1, values: mergedValues });
    }

    public static hasValues(queryPart: QueryPartData): boolean {
        return queryPart.values.length > 0;
    }

    public clone(): QueryPart {
        return new QueryPart(this);
    }

    public hasValues(): boolean {
        return QueryPart.hasValues(this);
    }

    public toAdvancedSearchTerm(): AdvancedSearchTerm {
        const operand: Operand = QueryPartStrategyToOperand(this.strategy);

        return {
            DottedKey: this.field,
            Values: this.values,
            Operand: operand
        };
    }

    toQueryPartData(): QueryPartData {
        return {
            field: this.field,
            strategy: this.strategy,
            values: this.values,
            handleQuotes: this.handleQuotes
        };
    }
}
